
import React, { useState, useMemo } from 'react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { ChangeOrder } from '../types.ts';
import { Plus, Trash2, FileText, MessageSquare, ArrowRight, DollarSign, Clock, CheckCircle2, Filter, ArrowUpDown, Sparkles, Loader2, ShieldCheck } from 'lucide-react';
import { formatCurrency } from '../services/financialService.ts';
import { Modal } from './Modal.tsx';
import { auditChangeOrder } from '../services/geminiService.ts';
import { useToast } from '../hooks/useToast.ts';
import { generateInvoice } from '../services/invoiceService.ts';
import { useRole } from '../hooks/useRole.ts';
import { Download, AlertCircle } from 'lucide-react';
import { ChangeOrderSchema } from '../schemas/validationSchemas.ts';
import { z } from 'zod';

export const ChangeOrdersPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const changeOrders = useStore(() => api.changeOrders.list(co => co.projectId === projectId), [projectId], 'changeOrders') || [];
  const { addToast } = useToast();
  const project = useStore(() => api.projects.get(projectId), [projectId], 'projects');
  const settingsList = useStore(() => api.settings.list(), [], 'settings');
  const user = settingsList?.[0];
  const { isForeman } = useRole();

  const [adding, setAdding] = useState(false);
  const [auditingId, setAuditingId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'date' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [newCO, setNewCO] = useState<Partial<ChangeOrder>>({
    coNumber: `CO-${String(changeOrders.length + 1).padStart(3, '0')}`,
    status: 'Draft',
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    title: '',
    description: '',
    communicationLog: ''
  });

  const filteredAndSorted = useMemo(() => {
    let result = [...changeOrders];

    if (filterStatus !== 'All') {
      result = result.filter(co => co.status === filterStatus);
    }

    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') {
        comparison = new Date(b.date).getTime() - new Date(a.date).getTime();
      } else {
        comparison = b.amount - a.amount;
      }
      return sortOrder === 'desc' ? comparison : -comparison;
    });

    return result;
  }, [changeOrders, filterStatus, sortBy, sortOrder]);

  const handleAudit = async (co: ChangeOrder) => {
    if (!co.id) return;
    setAuditingId(co.id);
    try {
      const summary = await auditChangeOrder(co.title, co.amount, co.description, co.communicationLog);
      await api.changeOrders.update(co.id, { accountabilitySummary: summary });
      addToast({ type: 'success', title: 'Accountability Refined', message: 'Formal audit narrative attached to Change Order.' });
    } catch (err) {
      addToast({ type: 'error', title: 'Audit Failed', message: 'Could not generate accountability statement.' });
    } finally {
      setAuditingId(null);
    }
  };

  const save = async () => {
    try {
      setValidationErrors({});
      const validated = ChangeOrderSchema.parse({
        ...newCO,
        projectId
      });

      await api.changeOrders.add({
        projectId,
        coNumber: validated.coNumber,
        title: validated.title,
        amount: validated.amount,
        status: validated.status as any,
        date: validated.date,
        description: validated.description,
        communicationLog: validated.communicationLog || ''
      });

      setAdding(false);
      addToast({ type: 'success', title: 'Scope Secured', message: 'Change Order successfully validated and registered.' });

      // Reset form
      setNewCO({
        coNumber: `CO-${String(changeOrders.length + 2).padStart(3, '0')}`,
        status: 'Draft',
        date: new Date().toISOString().split('T')[0],
        amount: 0,
        title: '',
        description: '',
        communicationLog: ''
      });

    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err: any) => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
        addToast({ type: 'error', title: 'Validation Failed', message: 'Please correct the highlighted fields to prevent bad data.' });
      }
    }
  };

  const totalApproved = changeOrders
    .filter(co => co.status === 'Approved')
    .reduce((acc, co) => acc + co.amount, 0);

  const handleInvoice = async () => {
    if (!project) return;
    const approved = changeOrders.filter(co => co.status === 'Approved');
    if (approved.length === 0) {
      addToast({ type: 'error', title: 'Action Blocked', message: 'Invoice requires at least one Approved Change Order.' });
      return;
    }

    try {
      const url = await generateInvoice(project, approved, user);
      window.open(url, '_blank');
      addToast({ type: 'success', title: 'Invoice Generated', message: 'PDF document ready for download.' });
    } catch (e) {
      console.error(e);
      addToast({ type: 'error', title: 'Generation Failed', message: 'Could not create invoice PDF.' });
    }
  };

  return (
    <div className="space-y-12 animate-in fade-in duration-500">
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 bg-white rounded-[3.5rem] border border-zinc-200 p-12 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-zinc-100 rounded-2xl text-black"><FileText size={24} /></div>
              <div>
                <h3 className="text-2xl font-black text-black tracking-tight uppercase">Change Order Registry</h3>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mt-1">Scope adjustments & formal directives</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-zinc-50 p-1 rounded-xl border border-zinc-100">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-transparent text-[9px] font-black uppercase tracking-widest px-3 py-1 outline-none appearance-none cursor-pointer hover:text-black transition-colors"
                >
                  <option value="All">All Statuses</option>
                  <option value="Draft">Draft</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Negotiating">Negotiating</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <button
                onClick={() => {
                  if (sortBy === 'date') setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                  setSortBy('date');
                }}
                className={`p-2 rounded-xl border transition-all ${sortBy === 'date' ? 'bg-black text-white border-black' : 'text-zinc-300 border-zinc-100 hover:text-black'}`}
                title="Sort by Date"
              >
                <Clock size={16} />
              </button>
              <button
                onClick={() => {
                  if (sortBy === 'amount') setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                  setSortBy('amount');
                }}
                className={`p-2 rounded-xl border transition-all ${sortBy === 'amount' ? 'bg-black text-white border-black' : 'text-zinc-300 border-zinc-100 hover:text-black'}`}
                title="Sort by Magnitude"
              >
                <ArrowUpDown size={16} />
              </button>
              <button onClick={() => setAdding(true)} className="px-6 py-3 bg-black text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:scale-105 transition-all shadow-lg ml-2">Register</button>
            </div>
          </div>

          <div className="space-y-6">
            {filteredAndSorted.map(co => (
              <div key={co.id} className="p-8 rounded-[2.5rem] border border-zinc-100 bg-zinc-50/30 hover:bg-white hover:border-zinc-200 hover:shadow-2xl transition-all duration-500 group">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{co.coNumber}</span>
                      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${co.status === 'Approved' ? 'bg-emerald-100 text-emerald-600' :
                        co.status === 'Rejected' ? 'bg-rose-100 text-rose-600' : 'bg-zinc-200 text-zinc-500'
                        }`}>{co.status}</span>
                    </div>
                    <h4 className="text-xl font-black text-black group-hover:translate-x-1 transition-transform">{co.title}</h4>
                  </div>
                  <div className="text-right flex flex-col items-end">
                    <div className="text-2xl font-black text-black">{formatCurrency(co.amount)}</div>
                    <div className="text-[10px] text-zinc-400 font-black uppercase tracking-widest mt-1">Impact Magnitude</div>
                    <button
                      onClick={() => handleAudit(co)}
                      disabled={auditingId === co.id}
                      className={`mt-4 flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[9px] font-black uppercase tracking-widest transition-all ${co.accountabilitySummary ? 'bg-zinc-900 text-emerald-400 border-zinc-800' : 'text-zinc-400 border-zinc-100 hover:text-black hover:border-black'
                        }`}
                    >
                      {auditingId === co.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      {co.accountabilitySummary ? 'Audit Trail Verified' : 'Audit for Accountability'}
                    </button>
                  </div>
                </div>

                {co.accountabilitySummary && (
                  <div className="mb-6 p-6 bg-zinc-900 rounded-2xl border border-zinc-800 shadow-inner animate-in slide-in-from-top-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ShieldCheck size={14} className="text-emerald-400" />
                      <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Accountability Directive</span>
                    </div>
                    <p className="text-xs text-zinc-300 font-medium leading-relaxed italic">{co.accountabilitySummary}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-8 py-6 border-y border-zinc-100">
                  <div>
                    <span className="text-[9px] font-black uppercase text-zinc-400 tracking-widest flex items-center gap-2 mb-2"><FileText size={12} /> Scope Detail</span>
                    <p className="text-xs text-zinc-600 font-medium leading-relaxed italic line-clamp-2">"{co.description}"</p>
                  </div>
                  <div>
                    <span className="text-[9px] font-black uppercase text-zinc-400 tracking-widest flex items-center gap-2 mb-2"><MessageSquare size={12} /> Communication Log</span>
                    <p className="text-xs text-zinc-500 font-bold leading-relaxed">{co.communicationLog || 'No entries.'}</p>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-6">
                  <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest flex items-center gap-2"><Clock size={12} /> Effective: {new Date(co.date).toLocaleDateString()}</span>
                  <button onClick={() => api.changeOrders.delete(co.id!)} className="p-2 text-zinc-200 hover:text-amber-500 transition-colors"><Trash2 size={18} /></button>
                </div>
              </div>
            ))}
            {filteredAndSorted.length === 0 && (
              <div className="py-24 text-center border-2 border-dashed border-zinc-100 rounded-[2.5rem]">
                <p className="text-zinc-400 font-black uppercase tracking-widest text-xs">No change orders found matching the filter criteria.</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-black rounded-[3.5rem] p-10 text-white shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity"><DollarSign size={120} /></div>
            <div className="relative z-10">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 mb-6">Pipeline Value</h4>
              <div className="text-4xl font-black tracking-tighter mb-2">{formatCurrency(totalApproved)}</div>
              <p className="text-zinc-500 text-xs font-bold leading-tight">Total aggregate value of approved modifications to original contract.</p>

              {!isForeman && (
                <button
                  onClick={handleInvoice}
                  disabled={totalApproved === 0}
                  className="mt-6 w-full py-3 bg-white text-black text-[10px] font-black uppercase tracking-widest rounded-xl hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 transition-all shadow-xl flex items-center justify-center gap-2"
                >
                  <Download size={14} /> Generate Invoice PDF
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-[3.5rem] p-10 border border-zinc-200 shadow-sm">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 mb-8">Contract Integrity</h4>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-zinc-50 flex items-center justify-center text-black shadow-inner"><CheckCircle2 size={24} /></div>
                <div>
                  <div className="text-sm font-black text-black">Audit Trail Active</div>
                  <p className="text-[10px] text-zinc-400 font-black uppercase mt-1">Communication logs documented</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-zinc-50 flex items-center justify-center text-black shadow-inner"><Clock size={24} /></div>
                <div>
                  <div className="text-sm font-black text-black">Awaiting Signature</div>
                  <p className="text-[10px] text-zinc-400 font-black uppercase mt-1">{(changeOrders.filter(c => c.status !== 'Approved').length)} pending proposals</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {adding && (
        <Modal isOpen={adding} onClose={() => setAdding(false)} title="Register Scope Modification" maxWidth="lg">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">CO ID</label>
                <input className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-black focus:bg-white outline-none" value={newCO.coNumber} onChange={e => setNewCO({ ...newCO, coNumber: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Date</label>
                <input type="date" className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-black focus:bg-white outline-none" value={newCO.date} onChange={e => setNewCO({ ...newCO, date: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Title / Description</label>
              <input className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-black focus:bg-white outline-none mb-4" placeholder="e.g. Additional Abatement Section B" value={newCO.title} onChange={e => setNewCO({ ...newCO, title: e.target.value })} />
              <textarea className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-bold text-sm focus:bg-white outline-none h-32" placeholder="Describe the technical scope adjustment..." value={newCO.description} onChange={e => setNewCO({ ...newCO, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Magnitude ($)</label>
                <input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-black focus:bg-white outline-none" value={newCO.amount} onChange={e => setNewCO({ ...newCO, amount: +e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Status</label>
                <select className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-black focus:bg-white outline-none appearance-none" value={newCO.status} onChange={e => setNewCO({ ...newCO, status: e.target.value as any })}>
                  <option value="Draft">Draft</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Negotiating">Negotiating</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block mb-2">Communication Log (Client/Contractor Directives)</label>
              <textarea className="w-full border border-zinc-100 bg-zinc-50 rounded-2xl p-4 font-bold text-xs focus:bg-white outline-none h-24" placeholder="Document specific negotiation notes, verbal approvals, or client pushback..." value={newCO.communicationLog} onChange={e => setNewCO({ ...newCO, communicationLog: e.target.value })} />
            </div>
            <button onClick={save} className="w-full bg-black text-white py-5 rounded-[2rem] font-black text-sm uppercase tracking-widest hover:scale-[1.02] shadow-2xl transition-all">Submit to Registry</button>
          </div>
        </Modal>
      )}
    </div>
  );
};
